import React, { FC, ReactElement, useEffect } from 'react';
import styled from 'styled-components';
import { Config } from './types';
import {
  NavType,
  Navigation,
  NavigationStateLink,
  NavigationLinkProps,
  anchorProperties,
  getNativeProps,
  NavigationConfig,
} from '@uhg-harmony/uitk-react';
import {
  Link,
  RouteComponentProps,
  useLocation,
  withRouter,
} from 'react-router-dom';
import { NavLink } from '../shared/types';
import { sanitizeUrl } from '../RichTextEditor/utils/url';
import { escape } from 'lodash';
import { useTrackWidget } from 'shared/util/analytics/useTrackWidget';
import { track } from 'shared/util/analytics/track';
import { navWidgetHandleClick, getLinkType } from './NavWidget.util';

// extracted this function so that it can be tested more easily
export const updateVisibility = (
  children: string[],
  currentMenuItem: number,
  currentPageContent: any,
  changeWidgetVisible: Function,
  siteId: number,
  pageId: number,
  widgetId: string
) => {
  children.forEach((child: string) => {
    const widget = currentPageContent[child];
    const isVisible = widget?.tabIndex === currentMenuItem;
    const currentVisibility = widget?.config.visible;

    if (currentVisibility !== isVisible) {
      changeWidgetVisible(
        siteId,
        pageId,
        widgetId,
        widgetId,
        isVisible,
        currentMenuItem
      );
    }
  });
};

const StyledNavWidget = styled.div<{
  borderData: BorderData;
  lineColor: string;
  textColor: string;
  isDifferentTextColorOnHover: boolean;
  hoverTextColor: string;
  type?: string;
}>`
  border-top: ${p =>
    p.borderData?.isTopBorderVisible ?? true
      ? `${p.borderData?.thickness}px solid`
      : 'none'};
  border-bottom: ${p =>
    p.borderData?.isBottomBorderVisible ?? true
      ? `${p.borderData?.thickness}px solid`
      : 'none'};
  border-left: ${p =>
    p.borderData?.isLeftBorderVisible ?? true
      ? `${p.borderData?.thickness}px solid`
      : 'none'};
  border-right: ${p =>
    p.borderData?.isRightBorderVisible ?? true
      ? `${p.borderData?.thickness}px solid`
      : 'none'};
  border-color: ${p => p.lineColor};
  border-radius: ${p => p.borderData?.radius}px;
  & .uitk-header {
    display: none;
  }
  & ul > li > a:hover,
  & ul > li > button:hover,
  & ul > li > a:focus,
  & ul > li > button:focus {
    box-shadow: none;
    background-color: transparent;
    color: ${p =>
      p.isDifferentTextColorOnHover ? p.hoverTextColor : p.textColor};
    outline: 3px solid
      ${p => (p.isDifferentTextColorOnHover ? p.hoverTextColor : p.textColor)};
    border-radius: 3px;
  }
  & button[data-testid^='nav-button'] > span > svg {
    fill: ${p =>
      p.isDifferentTextColorOnHover ? p.hoverTextColor : p.textColor};
  }
`;

const StyledVerticalNavigation = styled(Navigation)<{
  background: string;
  textColor: string;
  fontSize: number;
  font: string;
  showDividerLines: boolean;
  dividerLineColor: string;
  iconColor: string;
  width: number;
  isDifferentTextColorOnHover: boolean;
  hoverTextColor: string;
  dropdownItemsBackgroundColor: string;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikeThrough: boolean;
}>`
  box-shadow: none;
  width: 12rem;
  height: 100%;
  padding-top: 0;
  background: ${p => p.background};
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  background-clip: content-box;
  & ul,
  & ul > li > ul {
    border-style: none;
    cursor: pointer;
    max-width: 14em;
  }
  & a[data-testid^='nav-link'] {
    font-size: ${p => p.fontSize}px;
    color: ${p => p.textColor};
    font-family: ${p => p.font};
    font-weight: ${p => (p.bold ? 'bold' : 'normal')};
    font-style: ${p => (p.italic ? 'italic' : 'normal')};
    text-decoration: ${p => getTextDecoration(p)};
    border: none;
    margin: 0.25rem;
    border-radius: 3px;
  }
  & a[data-testid^='nav-link']:hover {
    font-size: ${p => p.fontSize}px;
    color: ${p =>
      p.isDifferentTextColorOnHover ? p.hoverTextColor : p.textColor};
    font-family: ${p => p.font};
    font-weight: ${p => (p.bold ? 'bold' : 'normal')};
    font-style: ${p => (p.italic ? 'italic' : 'normal')};
    text-decoration: ${p => getTextDecoration(p)};
    border: none;
    margin: 0.25rem;
    border-radius: 3px;
  }
  & button[data-testid^='nav-button'] {
    font-size: ${p => p.fontSize}px;
    color: ${p => p.textColor};
    font-family: ${p => p.font};
    font-weight: ${p => (p.bold ? 'bold' : 'normal')};
    font-style: ${p => (p.italic ? 'italic' : 'normal')};
    text-decoration: ${p => getTextDecoration(p)};
    border: none;
    margin: 0.25rem;
    border-radius: 3px;
    width: auto;
  }
  & button[data-testid^='nav-button']:hover {
    font-size: ${p => p.fontSize}px;
    color: ${p =>
      p.isDifferentTextColorOnHover ? p.hoverTextColor : p.textColor};
    font-family: ${p => p.font};
    font-weight: ${p => (p.bold ? 'bold' : 'normal')};
    font-style: ${p => (p.italic ? 'italic' : 'normal')};
    text-decoration: ${p => getTextDecoration(p)};
    border: none;
    margin: 0.25rem;
    border-radius: 3px;
    width: auto;
  }
  & > ul > li > button {
    text-align: left;
  }
  & > ul > li > a:before,
  & > ul > li > button:before {
    height: ${p => (p.showDividerLines ? '1px' : '0px')};
    background-color: ${p => p.dividerLineColor};
  }
  & button[data-testid^='nav-button'] > span > svg {
    fill: ${p => p.textColor};
  }
  & button[data-testid^='nav-button']:hover > span > svg,
  & button[data-testid^='nav-button']:focus > span > svg {
    fill: ${p =>
      p.isDifferentTextColorOnHover ? p.hoverTextColor : p.textColor};
  }
  & a[data-testid^='nav-link'] > span > span > img,
  & button[data-testid^='nav-button'] > span > span > img {
    max-width: ${p => p.fontSize * 1.75}px;
  }
  & button[data-testid^='nav-button'] + ul {
    font-size: ${p => p.fontSize}pt;
    color: ${p => p.textColor};
    font-family: ${p => p.font};
    background: ${p => p.dropdownItemsBackgroundColor};
    font-weight: ${p => (p.bold ? 'bold' : 'normal')};
    font-style: ${p => (p.italic ? 'italic' : 'normal')};
    text-decoration: ${p => getTextDecoration(p)};
    border: 2px solid transparent;
    border-radius: 3px;
  }
  & button[data-testid^='nav-button'],
  & button[data-testid^='nav-button'] > span > span + span {
    text-align: left;
  }
`;

const NavContent = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  @media only screen and (min-width: 980px) {
    flex-direction: row;
  }
`;

/**
 * Builds a root-to-leaf ancestor chain for section items.
 */
const getAncestorSectionChain = (
  items: NavLink[],
  parentSectionId: number | null | undefined
): NavLink[] => {
  const chain: NavLink[] = [];
  const visitedIds = new Set<number>();

  let currentParentId = parentSectionId;
  while (currentParentId != null && !visitedIds.has(currentParentId)) {
    visitedIds.add(currentParentId);
    const parent = items.find(item => item.id === currentParentId);
    if (!parent?.isSection) {
      break;
    }

    chain.unshift(parent);
    currentParentId = parent.parentSectionId;
  }

  return chain;
};

const buildVerticalNavQueryString = (
  items: NavLink[],
  targetItem: NavLink
): string => {
  if (!targetItem.routingId) return '';

  const params = new URLSearchParams();
  const ancestorSections = getAncestorSectionChain(
    items,
    targetItem.parentSectionId
  );
  const directAncestor = ancestorSections[ancestorSections.length - 1];

  if (targetItem.isSection) {
    if (directAncestor?.routingId) {
      params.set('tab', directAncestor.routingId);
      params.set('section', targetItem.routingId);
    } else {
      params.set('tab', targetItem.routingId);
    }
  } else {
    if (directAncestor?.routingId) {
      params.set('tab', directAncestor.routingId);
    }
    params.set('section', targetItem.routingId);
  }

  return params.toString();
};

const VERTICAL_NON_ROUTING_URL_PREFIX = '#nav-item-';

const getVerticalNonRoutingUrl = (itemId: number): string =>
  `${VERTICAL_NON_ROUTING_URL_PREFIX}${itemId}`;

export const isNavigationLinkActive = (url: string, route: string): boolean => {
  if (!url || !route) {
    return false;
  }

  // Vertical nav links are query-string only (e.g. ?tab=menu-1&section=link-3)
  // and should match against the current route's search params.
  if (url.startsWith('?')) {
    return route.includes(url);
  }

  const [routePath] = route.split('?');
  const [linkPath] = url.split('?');
  return routePath === linkPath;
};

export const getMenuItemsWithExpandedAncestors = (
  items: NavLink[],
  tab: string | null,
  section: string | null
): NavLink[] => {
  const targetRoutingId = section || tab;
  if (!targetRoutingId) {
    return items;
  }

  const targetItem = items.find(item => item.routingId === targetRoutingId);
  if (!targetItem) {
    return items;
  }

  const expandedSectionIds = new Set<number>();
  const addAncestorSectionIds = (
    parentSectionId: number | null | undefined
  ) => {
    let currentParentId = parentSectionId;
    const visitedIds = new Set<number>();

    while (currentParentId != null && !visitedIds.has(currentParentId)) {
      visitedIds.add(currentParentId);
      expandedSectionIds.add(currentParentId);
      const parentItem = items.find(item => item.id === currentParentId);
      currentParentId = parentItem?.parentSectionId;
    }
  };

  if (targetItem.isSection) {
    expandedSectionIds.add(targetItem.id);
  }
  addAncestorSectionIds(targetItem.parentSectionId);

  if (tab) {
    const tabItem = items.find(
      item => item.isSection && item.routingId === tab
    );
    if (tabItem) {
      expandedSectionIds.add(tabItem.id);
      addAncestorSectionIds(tabItem.parentSectionId);
    }
  }

  return items.map(item => {
    if (!item.isSection) {
      return item;
    }

    return {
      ...item,
      expanded: expandedSectionIds.has(item.id),
    };
  });
};

const Divider = styled.div<{ color: string }>`
  width: 1px;
  background-color: ${p => p.color};
  height: 100%;
`;

const StyledDiv = styled.div<{
  background: any;
  textColor: string;
  fontSize: number;
  font: string;
  showDividerLines: boolean;
  dividerLineColor: string;
  iconColor: string;
  fullScreen: boolean;
  height: number;
  isDifferentTextColorOnHover: boolean;
  hoverTextColor: string;
  dropdownItemsBackgroundColor: string;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikeThrough: boolean;
}>`
  max-width: 1280px;
  margin: 0 auto;
  & button[data-testid^='nav-button'],
  & a[data-testid^='nav-link'] {
    font-size: ${p => p.fontSize}px;
    color: ${p => p.textColor};
    font-family: ${p => p.font};
    font-weight: ${p => (p.bold ? 'bold' : 'normal')};
    font-style: ${p => (p.italic ? 'italic' : 'normal')};
    text-decoration: ${p => getTextDecoration(p)};
    border: 2px solid transparent;
  }

  & button[data-testid^='nav-button'] + ul {
    font-size: ${p => p.fontSize}pt;
    color: ${p => p.textColor};
    font-family: ${p => p.font};
    font-weight: ${p => (p.bold ? 'bold' : 'normal')};
    font-style: ${p => (p.italic ? 'italic' : 'normal')};
    text-decoration: ${p => getTextDecoration(p)};
    background: ${p => p.dropdownItemsBackgroundColor};
    border: 2px solid transparent;
    border-radius: 3px;
  }

  & a[data-testid^='nav-link']:hover {
    border: 2px solid #f4f4f4;
  }
  & button[data-testid^='nav-button']:hover {
    border: 2px solid #f4f4f4;
  }
  & button[data-testid^='nav-button'] > span > svg {
    fill: ${p => p.textColor};
  }
  & button[data-testid^='nav-button']:hover > span > svg,
  & button[data-testid^='nav-button']:focus > span > svg {
    fill: ${p =>
      p.isDifferentTextColorOnHover ? p.hoverTextColor : p.textColor};
  }
  & a[data-testid^='nav-link']:before,
  & button[data-testid^='nav-button']:before {
    height: ${p => (p.showDividerLines ? '1px' : '0px')};
    background-color: ${p => p.dividerLineColor};
  }
  & a[data-testid^='nav-link']:hover:before,
  & button[data-testid^='nav-button']:hover:before {
    border: none;
  }
  & > div + div > div > nav {
    padding-left: 0;
    margin-left: ${p => (p.fullScreen ? '2.5rem' : 0)};
    box-shadow: none;
  }
  & > div + div > div > nav > ul,
  & > div + div > div > nav > ul > li > button + ul {
    background: ${p => p.background};
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    background-clip: content-box;
  }
  & > div + div > div > nav > ul > li > button + ul {
    top: ${p => p.height}px;
  }
  & a[data-testid^='nav-link'] > span > span > img,
  & button[data-testid^='nav-button'] > span > span > img {
    width: ${p => p.fontSize * 1.75}px;
  }
`;

const StyledHorizontalNavigation = styled(Navigation)`
  & + div {
    height: 70px;
  }
  position: absolute;
  height: -webkit-fill-available;
  background: transparent;
  box-shadow: none;
`;

/**
 *   // Recursively builds navigation tree structure for rendering. Each section's children are processed in child order.
 * @param items - Array of navigation links
 * @param treeOrder - Array of root-level item IDs
 * @param style - Optional navigation style ('Vertical' or 'Horizontal')
 * @returns navigation config tree
 */
export function transformToNavigationConfig(
  items: NavLink[],
  treeOrder: number[],
  style?: string
): NavigationConfig['links'] {
  const transformChildrenRecursive = (
    parentId: number | null
  ): NavigationConfig['links'] => {
    const children = items
      .filter(item => item.parentSectionId === parentId)
      .sort((a, b) => {
        const positionA = (
          items.find(i => i.id === parentId)?.childOrder || []
        ).indexOf(a.id);
        const positionB = (
          items.find(i => i.id === parentId)?.childOrder || []
        ).indexOf(b.id);
        return positionA - positionB;
      });
    return children.map(child => {
      const {
        id,
        displayText: label,
        url,
        isSection,
        icon,
        expanded,
        routingId,
      } = child;
      const safeUrl = icon?.url ? sanitizeUrl(icon.url) : undefined;
      const safeAlt = icon?.alt ? escape(icon.alt) : undefined;
      const customContent = safeUrl ? (
        <img src={safeUrl} alt={safeAlt} />
      ) : (
        undefined
      );
      if (isSection) {
        return {
          label,
          customContent,
          links: transformChildrenRecursive(child.id),
          expanded: expanded ?? false,
        };
      } else {
        let modifiedUrl = url;
        if (style === 'Vertical') {
          if (!routingId) {
            // Preserve external URLs so RoutableLink can open them in a new tab;
            // use the anchor-based id only for internal/non-absolute links.
            modifiedUrl = getLinkType(url).isExternal
              ? url
              : getVerticalNonRoutingUrl(id);
          } else {
            const query = buildVerticalNavQueryString(items, child);
            modifiedUrl = query ? `?${query}` : undefined;
          }
        }
        return {
          label,
          url: modifiedUrl,
          customContent,
        };
      }
    });
  };

  const result = treeOrder
    .map(rootId => {
      const rootItem = items.find(item => item.id === rootId);
      if (!rootItem) return null;

      const {
        id,
        displayText: label,
        url,
        isSection,
        icon,
        expanded,
        routingId,
      } = rootItem;
      const safeUrl = icon?.url ? sanitizeUrl(icon.url) : undefined;
      const safeAlt = icon?.alt ? escape(icon.alt) : undefined;
      const customContent = safeUrl ? (
        <img src={safeUrl} alt={safeAlt} />
      ) : (
        undefined
      );

      if (isSection) {
        return {
          label,
          customContent,
          links: transformChildrenRecursive(rootItem.id),
          expanded: expanded ?? false,
        };
      } else {
        let modifiedUrl = url;
        if (style === 'Vertical') {
          if (!routingId) {
            // Preserve external URLs so RoutableLink can open them in a new tab;
            // use the anchor-based id only for internal/non-absolute links.
            modifiedUrl = getLinkType(url).isExternal
              ? url
              : getVerticalNonRoutingUrl(id);
          } else {
            const query = buildVerticalNavQueryString(items, rootItem);
            modifiedUrl = query ? `?${query}` : undefined;
          }
        }
        return {
          label,
          url: modifiedUrl,
          customContent,
        };
      }
    })
    .filter(Boolean); // Remove null values

  return result;
}

// check here if the item should be rendered based on user IDP
function shouldRenderNavItem(item: NavLink, user: any): boolean {
  if (item?.visible) return true;
  const selectedIdps = Array.isArray(item?.selectedIdps)
    ? item?.selectedIdps?.map((idp: any) => idp?.value)
    : [];
  return selectedIdps.includes(user?.profile?.identity_provider);
}

// this checks all nav items recursively; if a section is not visible, it will not render its children
function filterNavItemsRecursive(items: NavLink[], user: any): NavLink[] {
  return items
    .map(item => {
      if (item?.isSection) {
        if (!shouldRenderNavItem(item, user)) return null;
        const children = items.filter(
          child => child?.parentSectionId === item?.id
        );
        const filteredChildren = filterNavItemsRecursive(children, user);
        return {
          ...item,
          childOrder: (item?.childOrder || []).filter(childId =>
            filteredChildren.some(child => child && child.id === childId)
          ),
        };
      } else {
        return shouldRenderNavItem(item, user) ? item : null;
      }
    })
    .filter(Boolean) as NavLink[];
}

export function background(backgroundData, backgroundImage) {
  if (backgroundData.style === 'Color') {
    return backgroundData?.color;
  }
  if (backgroundData.style === 'Image') {
    return `url('${backgroundImage?.url}')`;
  }
  return 'transparent';
}

const getTextDecoration = ({
  underline,
  strikeThrough,
}: {
  underline: boolean;
  strikeThrough: boolean;
}): string => {
  if (underline) return 'underline';
  if (strikeThrough) return 'line-through';
  return 'none';
};

const NavWidget: React.FC<WidgetRenderProps<Config> &
  RouteComponentProps<any>> = (
  props: WidgetRenderProps<Config> & RouteComponentProps<any>
): ReactElement => {
  const {
    width,
    height,
    borderData,
    backgroundData,
    backgroundImage,
    lineColor,
    font,
    fontSize,
    textColor,
    showDividerLines,
    dividerLineColor,
    treeOrder,
    iconColor,
    paddingData,
    menuItems,
    style,
    enableFullScreen,
    isDifferentTextColorOnHover,
    hoverTextColor,
    visible,
    isCustomWidget,
    showVerticalDivider,
    verticalDividerLineColor,
    dropdownItemsBackgroundColor,
    bold,
    italic,
    underline,
    strikeThrough,
  } = props.widgetState.config;
  const {
    currentPage,
    user,
    editing,
    changeWidgetVisible,
    onChange,
    siteId,
    history,
  } = props;

  const currentUser = user;
  const location = useLocation();

  const useCurrentRoute = () => {
    const { pathname, search } = location;
    return `${pathname}${search}`;
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    const section = params.get('section');

    updateMenuStateFromURL(tab, section);
  }, [location.search, menuItems, props.widgetState.config.currentMenuItem]);

  const updateMenuStateFromURL = (
    tab: string | null,
    section: string | null
  ) => {
    const targetRoutingId = section || tab;
    if (!targetRoutingId) {
      return;
    }

    const targetItem = menuItems.find(
      item => item.routingId === targetRoutingId
    );

    if (!targetItem) {
      return;
    }

    const updatedMenuItems = getMenuItemsWithExpandedAncestors(
      menuItems,
      tab,
      section
    );

    // Check if anything changed
    const hasChanges = !menuItems.every(
      (item, index) => item.expanded === updatedMenuItems[index].expanded
    );

    if (
      hasChanges ||
      props.widgetState.config.currentMenuItem !== targetItem.id
    ) {
      // Update the widget config with new expanded states and active item
      onChange(
        {
          ...props.widgetState.config,
          menuItems: updatedMenuItems,
          currentMenuItem: targetItem.id,
        },
        props.widgetState.id,
        false
      );

      // Update child widget visibility
      const navWidgetChildren =
        currentPage.content[props.widgetState.id].children;

      updateVisibility(
        navWidgetChildren,
        targetItem.id,
        currentPage.content,
        changeWidgetVisible,
        siteId,
        currentPage.pageId,
        props.widgetState.id
      );
    }
  };

  const currentQueryParams = new URLSearchParams(location.search);
  const menuItemsForRoute =
    style === 'Vertical'
      ? getMenuItemsWithExpandedAncestors(
          menuItems,
          currentQueryParams.get('tab'),
          currentQueryParams.get('section')
        )
      : menuItems;

  const filteredMenuItems = filterNavItemsRecursive(
    menuItemsForRoute,
    currentUser
  );

  const verticalExpansionSignature = menuItemsForRoute
    .filter(item => item.isSection)
    .map(item => `${item.id}:${item.expanded ? '1' : '0'}`)
    .join('|');

  let navigationLinks = transformToNavigationConfig(
    menuItemsForRoute,
    treeOrder,
    style
  );

  // check if widget is displaying in builder or client; in builder, we want to show all items
  if (!editing) {
    navigationLinks = transformToNavigationConfig(
      filteredMenuItems,
      treeOrder,
      style
    );
  }

  const visibleChange = (currentMenuItem: number) => {
    const widgetId = props.widgetState.id;
    const pageContent = currentPage?.content;
    // Guard against missing page content (e.g. simplified props in tests / preview contexts).
    if (!pageContent?.[widgetId]) return; // optional chain guard
    const navWidgetChildren = pageContent[widgetId].children;
    updateVisibility(
      navWidgetChildren,
      currentMenuItem,
      pageContent,
      changeWidgetVisible,
      siteId,
      currentPage.pageId,
      widgetId
    );
  };

  // Tracking hook
  const trackNavWidget = useTrackWidget({
    tracking: (props as any).tracking,
    widgetState: props.widgetState,
    currentPage,
    site: (props as any).site,
  });

  const handleTrackedClick = (
    e: React.MouseEvent<HTMLElement>,
    link: NavigationStateLink
  ) => {
    link.active = true;
    const clickedLabel = (
      link?.label ||
      e.currentTarget.innerText ||
      ''
    ).trim();

    // Track clicks without triggering util-level state changes; routing state is handled below.
    navWidgetHandleClick({
      editing: editing || false,
      style,
      menuItems,
      clickedLabel,
      trackNavWidget,
      onChange: undefined,
      widgetState: undefined,
      visibleChange: undefined,
    });

    if (style !== 'Vertical') {
      if (editing) e.preventDefault();
      return;
    }

    // External links in vertical nav should open in a new tab via RoutableLink's
    // <a target="_blank">. Only suppress default behaviour during editing, matching
    // the horizontal-nav convention above.
    if (getLinkType(link?.url as string).isExternal) {
      if (editing) e.preventDefault();
      return;
    }

    e.preventDefault();

    const nonRoutingItemId =
      typeof link?.url === 'string' &&
      link.url.startsWith(VERTICAL_NON_ROUTING_URL_PREFIX)
        ? Number.parseInt(
            link.url.slice(VERTICAL_NON_ROUTING_URL_PREFIX.length),
            10
          )
        : undefined;

    const clickedItem = Number.isFinite(nonRoutingItemId)
      ? menuItems.find(item => item.id === nonRoutingItemId)
      : menuItems.find(item => item.displayText === clickedLabel);
    if (!clickedItem) {
      return;
    }

    let expandedChanged = false;
    const updatedMenuItems = menuItems.map(mi => ({ ...mi }));
    const clickedIndex = updatedMenuItems.findIndex(
      mi => mi.id === clickedItem.id
    );

    if (clickedItem.isSection) {
      if (clickedIndex !== -1) {
        updatedMenuItems[clickedIndex].expanded = !updatedMenuItems[
          clickedIndex
        ].expanded;
        expandedChanged = true;
      }
    } else if (clickedItem.parentSectionId) {
      let parentId = clickedItem.parentSectionId;
      while (parentId != null) {
        const parentIndex = updatedMenuItems.findIndex(m => m.id === parentId);
        if (parentIndex === -1) break;
        if (!updatedMenuItems[parentIndex].expanded) {
          updatedMenuItems[parentIndex].expanded = true;
          expandedChanged = true;
        }
        parentId = updatedMenuItems[parentIndex].parentSectionId;
      }
    }

    const currentMenuItem = clickedItem.id;
    if (expandedChanged || currentMenuItem !== undefined) {
      onChange(
        {
          ...props.widgetState.config,
          menuItems: updatedMenuItems,
          currentMenuItem,
        },
        props.widgetState.id,
        false
      );
      visibleChange(currentMenuItem);
    }

    if (clickedItem.routingId) {
      const params = new URLSearchParams(
        buildVerticalNavQueryString(menuItems, clickedItem)
      );

      history.push({
        pathname: location.pathname,
        search: params.toString(),
      });
    } else if (location.search) {
      // clear stale routing params so the URL-change useEffect doesn't re-activate
      // the previously-routed item after clicking a non-routed item.
      history.push({
        pathname: location.pathname,
        search: '',
      });
    }

    if (editing) {
      e.preventDefault();
    }
  };

  const RoutableLink: FC<NavigationLinkProps> = item => {
    const { children, url } = item;
    const anchorProps = getNativeProps(item, anchorProperties);

    // Block javascript: and other potentially malicious URL schemes
    if (url && /^javascript:/i.test(url)) {
      return null;
    }

    const { isExternal, isSameDomain, relativePath } = getLinkType(url);

    if (isExternal) {
      // External URL — open in new tab
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          {...anchorProps}
        >
          {children}
        </a>
      );
    }

    if (isSameDomain && relativePath) {
      // Absolute URL sharing the current origin — use React Router for SPA navigation
      return (
        <Link to={relativePath} {...anchorProps}>
          {children}
        </Link>
      );
    }

    // Default: relative path, fragment, or malformed URL — treat as internal
    return (
      <Link to={url || ''} {...anchorProps}>
        {children}
      </Link>
    );
  };

  const navigationConfig = {
    linkAs: RoutableLink,
    links: navigationLinks,
    onLinkClick: handleTrackedClick,
    isActive: isNavigationLinkActive,
  };

  if (visible === false || isCustomWidget) {
    return <></>;
  }

  return (
    <StyledNavWidget
      data-test-id="nav-widget"
      borderData={borderData}
      lineColor={lineColor}
      textColor={textColor}
      isDifferentTextColorOnHover={isDifferentTextColorOnHover}
      hoverTextColor={hoverTextColor}
      id={'NavWidget'}
      className={'NavWidget'}
      style={{
        height: '100%',
        width: '100%',
        paddingTop: `${paddingData?.top}px`,
        paddingRight: `${paddingData?.right}px`,
        paddingBottom: `${paddingData?.bottom}px`,
        paddingLeft: `${paddingData?.left}px`,
        background:
          style === 'Horizontal'
            ? background(backgroundData, backgroundImage)
            : 'none',
      }}
    >
      {style === 'Vertical' && (
        <NavContent
          style={{
            maxHeight: `${height -
              (paddingData?.top || 0) -
              (paddingData?.bottom || 0)}px`,
            overflowY: 'auto',
          }}
        >
          <StyledVerticalNavigation
            key={`${location.pathname}${location.search}-${verticalExpansionSignature}`}
            data-test-id="vertical-navigation"
            font={font}
            bold={bold}
            italic={italic}
            underline={underline}
            strikeThrough={strikeThrough}
            fontSize={fontSize}
            textColor={textColor}
            iconColor={iconColor}
            showDividerLines={showDividerLines}
            dividerLineColor={dividerLineColor}
            background={background(backgroundData, backgroundImage)}
            useLocation={useCurrentRoute}
            variant={NavType.VERTICAL}
            config={navigationConfig}
            width={width}
            isDifferentTextColorOnHover={isDifferentTextColorOnHover}
            hoverTextColor={hoverTextColor || 'black'}
            dropdownItemsBackgroundColor={dropdownItemsBackgroundColor}
          />
          {showVerticalDivider && (
            <Divider
              data-test-id="vertical-divider"
              color={verticalDividerLineColor}
            />
          )}
        </NavContent>
      )}
      {style === 'Horizontal' && (
        <StyledDiv
          data-test-id="horizontal-navigation"
          font={font}
          bold={bold}
          italic={italic}
          underline={underline}
          strikeThrough={strikeThrough}
          fontSize={fontSize}
          textColor={textColor}
          hoverTextColor={hoverTextColor}
          iconColor={iconColor}
          showDividerLines={showDividerLines}
          dividerLineColor={dividerLineColor}
          background={background(backgroundData, backgroundImage)}
          fullScreen={enableFullScreen}
          height={height}
          isDifferentTextColorOnHover={isDifferentTextColorOnHover}
          dropdownItemsBackgroundColor={dropdownItemsBackgroundColor}
        >
          <StyledHorizontalNavigation
            data-test-id="styled-horizontal-navigation"
            config={navigationConfig}
            useLocation={useCurrentRoute}
            variant={NavType.HORIZONTAL}
          />
        </StyledDiv>
      )}
    </StyledNavWidget>
  );
};

export default withRouter(track()(NavWidget));
